// The phone's per-lot desired state (BL-2681, ADR 070 — Ingest Protocol § 7, ADR 066 — Offline Mode § 4):
// the queue holds where each lot should end up plus the value each field had when offline editing
// began, and the one lot-state request is the difference. Pure, so the collapse, the send identity
// and the settle rule are testable without a store or a network.
import { lotPatchValuesEqual } from './offline-lot-edit';
import {
  AUCTION_CREATE_PERSISTED_KEYS,
  HOME_CREATE_PERSISTED_KEYS,
} from '@auctogon/core-admin/schemas/lot-state-write';

import type { ChangeQueueJson, ChangeQueueLabel } from './change-queue-entry';
import { MAX_REPLAY_ATTEMPTS, type ReplayError } from './change-queue-outcome';
import { EMPTY_MEDIA, foldMediaSave, isMediaEmpty, mediaOf, positionsToSend, settleMedia, type LotMedia, type MediaSave } from './lot-state-media';

/** Where a lot's desired state is in its send. */
export type LotStateStatus = 'pending' | 'syncing' | 'failed' | 'refused';

/** One field a conflict names: what the phone wants and what the server holds now. */
export interface LotConflictField {
  field: string;
  mine: ChangeQueueJson;
  /** The server's value; null when the field is empty there, or when `theirsUnread`. */
  theirs: ChangeQueueJson;
  /** True when the server's value could not be read, so the row does not call the field empty. */
  theirsUnread?: boolean;
}

/** The op id of the send in flight, and the content it was minted for. */
export interface LotStateSend {
  clientOpId: string;
  digest: string;
}

/** One lot's desired final state, as the device store keeps it. */
export interface LotDesiredState {
  /** The lot's id, made on the phone for a create. */
  lotId: string;
  tenantId: string;
  kind: 'create' | 'edit';
  /** The auction a create places the lot in, else null (an unassigned home lot, or an edit). */
  auctionId: string | null;
  /** Each field the operator changed, at its latest value. */
  fields: Record<string, ChangeQueueJson>;
  /** Each changed field's value when offline editing began (ADR 066 § 4.1). First touch wins. */
  bases: Record<string, ChangeQueueJson>;
  /** True once the operator chose Overwrite: the request carries `overwrite` and no bases. */
  overwrite: boolean;
  /** The fields Overwrite covers. A field edited after the choice keeps its own conflict check. */
  overwriteFields: string[];
  /** The photos and videos to send, the ids to remove and the final order (BL-2815). Absent on a row saved before. */
  media?: LotMedia;
  send: LotStateSend | null;
  conflict: LotConflictField[] | null;
  label: ChangeQueueLabel;
  status: LotStateStatus;
  error: ReplayError | null;
  attempts: number;
  createdAt: number;
  updatedAt: number;
}

/** The body of `PUT /api/lots/:id/state` for a lot's fields (the request schema is core-admin's). */
export interface LotStateRequest {
  tenantId: string;
  clientOpId: string;
  auctionId?: string;
  fields: Record<string, ChangeQueueJson>;
  baseValues?: Record<string, ChangeQueueJson>;
  overwrite?: true;
  /** Media ids the operator removed (an edit only). */
  removedMediaIds?: string[];
  /** Media id to its index in the final order, when the order names a photo the server holds (an edit only). */
  positions?: Record<string, number>;
}

function stableJson(value: ChangeQueueJson): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  if (typeof value === 'object' && value !== null) {
    const body = Object.keys(value)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableJson(value[k])}`);
    return `{${body.join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/**
 * The fields that would actually change the server: a field back at its base is no change, and a
 * create has no bases so keeps every field.
 *
 * @param state - the lot's desired state.
 * @returns the changed fields at their desired values.
 */
export function effectiveFields(state: LotDesiredState): Record<string, ChangeQueueJson> {
  return Object.fromEntries(
    Object.entries(state.fields).filter(([key, value]) => !(key in state.bases) || !lotPatchValuesEqual(value, state.bases[key])),
  );
}

/**
 * Whether the state has nothing left to send: an edit whose every field is back at its base. A
 * create is never empty, because the lot itself is the write.
 *
 * @param state - the lot's desired state.
 * @returns true when no request is needed.
 */
export function isEmptyWrite(state: LotDesiredState): boolean {
  return state.kind === 'edit' && Object.keys(effectiveFields(state)).length === 0 && isMediaEmpty(mediaOf(state));
}

/**
 * Fold one Save into the lot's desired state: the latest value of each field, the first base of
 * each. A lot that is refused, failed or mid-send keeps that status and still takes the Save, so
 * the operator's work is never dropped by a retry in progress.
 *
 * @param state - the lot's state so far, or null for its first Save.
 * @param input.lotId - the lot's id.
 * @param input.tenantId - the house.
 * @param input.kind - 'create' for a lot made offline, else 'edit'. A create stays a create.
 * @param input.auctionId - the auction a create places the lot in.
 * @param input.fields - the fields this Save changed, at their new values.
 * @param input.media - the photos and videos this Save added and removed, and the order it left them in.
 * @param input.baseValues - the value each of them had on the device before this Save.
 * @param input.label - how the queue names the lot, captured now.
 * @param input.now - the time of the Save, in epoch milliseconds.
 * @returns the new state.
 */
export function applySave(
  state: LotDesiredState | null,
  input: {
    lotId: string;
    tenantId: string;
    kind: 'create' | 'edit';
    auctionId?: string | null;
    fields: Record<string, ChangeQueueJson>;
    baseValues: Record<string, ChangeQueueJson>;
    media?: MediaSave;
    label: ChangeQueueLabel;
    now: number;
  },
): LotDesiredState {
  const media = input.media ? foldMediaSave(state ? mediaOf(state) : EMPTY_MEDIA, input.media).media : undefined;
  if (state == null) {
    return {
      lotId: input.lotId,
      tenantId: input.tenantId,
      kind: input.kind,
      auctionId: input.auctionId ?? null,
      fields: { ...input.fields },
      bases: input.kind === 'create' ? {} : { ...input.baseValues },
      overwrite: false,
      overwriteFields: [],
      ...(media ? { media } : {}),
      send: null,
      conflict: null,
      label: input.label,
      status: 'pending',
      error: null,
      attempts: 0,
      createdAt: input.now,
      updatedAt: input.now,
    };
  }
  const bases = { ...state.bases };
  if (state.kind === 'edit') {
    for (const [key, value] of Object.entries(input.baseValues)) {
      if (!(key in bases)) bases[key] = value;
    }
  }
  return {
    ...state,
    fields: { ...state.fields, ...input.fields },
    bases,
    ...(media ? { media } : {}),
    label: input.label,
    updatedAt: input.now,
  };
}

/**
 * Build the lot-state request for a state. After Overwrite the request carries `overwrite` and
 * only the fields Overwrite covers, because the server drops every base in a request that
 * carries it (BUG-2067): a field edited later keeps its conflict check in the next request.
 *
 * @param state - the lot's desired state.
 * @param clientOpId - the send's op id, from {@link sendIdentity}.
 * @returns the request body.
 */
export function buildStateRequest(state: LotDesiredState, clientOpId: string): LotStateRequest {
  const changed = effectiveFields(state);
  if (state.overwrite) {
    const fields = Object.fromEntries(Object.entries(changed).filter(([key]) => state.overwriteFields.includes(key)));
    // Every overwritten field reverted since: the server refuses an empty overwrite, so the rest goes as an ordinary checked edit.
    if (Object.keys(fields).length > 0) return { tenantId: state.tenantId, clientOpId, fields, overwrite: true };
  }
  if (state.kind === 'create') {
    // The chosen door stores only some keys and the server refuses the rest, so the others wait
    // for the follow-up edit (settleSent keeps them).
    const stored = state.auctionId != null ? AUCTION_CREATE_PERSISTED_KEYS : HOME_CREATE_PERSISTED_KEYS;
    const fields = Object.fromEntries(Object.entries(changed).filter(([key]) => stored.has(key)));
    return state.auctionId != null
      ? { tenantId: state.tenantId, clientOpId, auctionId: state.auctionId, fields }
      : { tenantId: state.tenantId, clientOpId, fields };
  }
  const request: LotStateRequest = { tenantId: state.tenantId, clientOpId, fields: changed };
  const media = mediaOf(state);
  if (media.removed.length > 0) request.removedMediaIds = [...media.removed];
  const positions = positionsToSend(media);
  if (positions != null) request.positions = positions;
  const baseValues = Object.fromEntries(Object.entries(state.bases).filter(([key]) => key in changed));
  if (Object.keys(baseValues).length > 0) request.baseValues = baseValues;
  return request;
}

/**
 * The op id this send uses. A retry of unchanged content reuses the op id, so the server answers
 * it as a repeat; any change since the first send gets a new one (ADR 070 § 1).
 *
 * @param state - the lot's desired state.
 * @param mint - makes a new lowercase v4 UUID.
 * @returns the op id, and the state with that send recorded (persist it before the request leaves).
 */
export function sendIdentity(
  state: LotDesiredState,
  mint: () => string,
): { clientOpId: string; state: LotDesiredState } {
  const digest = stableJson(buildStateRequest(state, '') as unknown as ChangeQueueJson);
  if (state.send != null && state.send.digest === digest) return { clientOpId: state.send.clientOpId, state };
  const clientOpId = mint();
  return { clientOpId, state: { ...state, send: { clientOpId, digest } } };
}

/**
 * What a lot made on the server holds for a field its create never set. A created lot's field the
 * create door does not store, still at this value, is already what the server has, so no follow-up
 * edit is owed (a null mileage unit reads as miles, ADR 035).
 */
const NEW_LOT_DEFAULTS: Readonly<Record<string, ChangeQueueJson>> = { mileageUnit: 'miles' };

/**
 * The request landed: the server now holds what it carried. What is left is whatever the operator
 * changed to something else meanwhile, and a created lot's fields the create door does not store,
 * which go out as an edit next. Each field still owed is measured against the value the server
 * holds; a field it never had a value for is sent with no base.
 *
 * @param state - the lot's state as it is now.
 * @param sent - the fields the request carried (`request.fields`), at the values it sent.
 * @param sentMedia - the removed ids and position map the request carried (BL-2815).
 * @returns null when nothing is left to send, else the state still to send.
 */
export function settleSent(
  state: LotDesiredState,
  sent: Record<string, ChangeQueueJson>,
  sentMedia: { removedMediaIds: string[]; positions: Record<string, number> | null } = { removedMediaIds: [], positions: null },
): LotDesiredState | null {
  const fields = Object.fromEntries(
    Object.entries(state.fields).filter(
      ([key, value]) =>
        (!(key in sent) || !lotPatchValuesEqual(value, sent[key])) &&
        // BL-2681 AC 8: an untouched default is not a change, so a create's settle leaves no extra request.
        !(state.kind === 'create' && !(key in sent) && key in NEW_LOT_DEFAULTS && lotPatchValuesEqual(value, NEW_LOT_DEFAULTS[key])),
    ),
  );
  const media = settleMedia(mediaOf(state), sentMedia);
  if (Object.keys(fields).length === 0 && isMediaEmpty(media)) return null;
  const bases: Record<string, ChangeQueueJson> = {};
  for (const key of Object.keys(fields)) {
    const base = key in sent ? sent[key] : state.bases[key];
    if (base !== undefined) bases[key] = base;
  }
  return {
    ...state,
    kind: 'edit',
    auctionId: null,
    fields,
    bases,
    media,
    overwrite: false,
    overwriteFields: [],
    send: null,
    conflict: null,
    status: 'pending',
    error: null,
    attempts: 0,
  };
}

/**
 * @param state - the lot's desired state.
 * @returns the state marked as being sent.
 */
export function markSyncing(state: LotDesiredState): LotDesiredState {
  return { ...state, status: 'syncing' };
}

/**
 * The server said a field changed under the phone (409 `STALE_FIELDS`).
 *
 * @param state - the lot's desired state.
 * @param error - what the server said.
 * @param conflict - each named field, the phone's value and the server's.
 * @returns the state, refused, until the operator chooses Overwrite or Keep.
 */
export function markRefused(state: LotDesiredState, error: ReplayError, conflict: LotConflictField[]): LotDesiredState {
  return { ...state, status: 'refused', error, conflict };
}

/**
 * The server said no for its own reason. The operator can Retry or Discard.
 *
 * @param state - the lot's desired state.
 * @param error - what the server said.
 * @returns the state, failed.
 */
export function markFailed(state: LotDesiredState, error: ReplayError): LotDesiredState {
  return { ...state, status: 'failed', error };
}

/**
 * A server error: the lot stays queued for the next pass, and fails after
 * {@link MAX_REPLAY_ATTEMPTS} tries in all.
 *
 * @param state - the lot's desired state.
 * @param error - what the server said.
 * @returns the state, pending again or failed.
 */
export function markRetryLater(state: LotDesiredState, error: ReplayError): LotDesiredState {
  const attempts = state.attempts + 1;
  return { ...state, attempts, error, status: attempts >= MAX_REPLAY_ATTEMPTS ? 'failed' : 'pending' };
}

/**
 * The operator chose Overwrite on a refused lot: resend the fields in conflict with no base check,
 * as a new send. A field edited after the refusal keeps its own check and goes in the next request
 * (BUG-2067).
 *
 * @param state - the lot's desired state.
 * @returns the state, pending, covering the conflicted fields (every changed field when none are named).
 */
export function markOverwrite(state: LotDesiredState): LotDesiredState {
  const changed = Object.keys(effectiveFields(state));
  const conflicted = (state.conflict ?? []).map((c) => c.field).filter((field) => changed.includes(field));
  return {
    ...state,
    overwrite: true,
    overwriteFields: conflicted.length > 0 ? conflicted : changed,
    send: null,
    conflict: null,
    error: null,
    attempts: 0,
    status: 'pending',
  };
}

/**
 * The operator chose Retry on a failed lot.
 *
 * @param state - the lot's desired state.
 * @returns the state, pending, with its attempts cleared.
 */
export function markRetry(state: LotDesiredState): LotDesiredState {
  return { ...state, status: 'pending', error: null, attempts: 0 };
}
